<template>
  <div>
    <h3 style="padding: 1rem">Verlinkte Rabatte</h3>
    <div>
      <div class="new-subscription">
        <v-select
          class="discount-filter"
          :items="discounts"
          v-model="selectedDiscountId"
          placeholder="Suche Rabatt"
        />
        <v-btn
          id="assign-discount-btn"
          :disabled="!selectedDiscountId"
          @click="assignDiscountToPharmacy()">
            Rabatte verlinken
        </v-btn>
      </div>
      <div class="subscribed-discounts">
        <div class="no-discounts" v-if="Object.keys(appliedDiscounts).length === 0">
          Es können keine Rabatte angezeigt werden
        </div>
        <v-simple-table v-else class="discount-table">
          <template v-slot:default>
            <thead>
              <tr class="vuetify-wrapper">
                <th>Rabatt Name</th>
                <th>Rabatt Beschreibung</th>
                <th>Rabatt Typ</th>
                <th>Rabatt Wert</th>
                <th>ND Rabatt Typ</th>
                <th>ND Rabatt Wert</th>
                <th>Rabatt Startdatum</th>
                <th>Rabatt Endtermin</th>
                <th>Rabatt Ist Aktiv?</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(discount, id) in appliedDiscounts"
                :key="id"
                class="vuetify-wrapper"
                :data-id="id"
              >
                <td>{{ discount.discountName }}</td>
                <td>{{ discount.description }}</td>
                <td>{{ translateDiscountType(discount.discountType) }}</td>
                <td>{{ discountValueDisplay(discount) }}</td>
                <td>{{ translateDiscountType(discount.flatRateDiscountType) }}</td>
                <td>{{ flatRateDiscountValueDisplay(discount) }}</td>
                <td>{{ discount.startDate }}</td>
                <td>{{ discount.endDate }}</td>
                <td>{{ discount.isActive }}</td>
                <td>
                  <v-btn
                    small
                    depressed
                    class="unlink-discount-btn"
                    color="error"
                    @click="isUnlinkDiscountDialogOpen = true">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
                <unlink-discount-dialog
                  v-model="isUnlinkDiscountDialogOpen"
                  :discountId="id"
                  :pharmacyId="pharmacyId" />
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <h3 style="padding: 1rem; margin-top: 2rem">Einmalige Gutschrift (Terminansicht)</h3>
    <v-data-table
      :headers="headers"
      :items="oneTimeDiscountsAppointments"
      :loading="isLoading"
      class="elevation-1 v-application--is-ltr"
      no-data-text="Keine Suchergebnisse"
      loading-text="Wird geladen..."
      :items-per-page="5"
      :footer-props="{
        'items-per-page-options':[5, 10, 25],
        'show-first-last-page': true,
        'show-current-page': true,
        'items-per-page-text': 'Zeilen pro Seite'
      }"
    >
      <template #item.created="{ item }">
        {{ $formatDate(item.created, 'DD.MM.YYYY HH:mm') }}
      </template>

      <template #item.date="{ item }">
        {{ $formatDate(item.date, 'DD-MM-YYYY') }}
      </template>

      <template #item.mediation_charge="{ item }">
        {{ item.mediation_charge?.toFixed(2)?.replace('.', ',') }} €
      </template>

      <template #item.wf_state="{ item }">
        {{ translateWfState(item.wf_state) }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from "axios";
import { mapMutations } from "vuex";
import UnlinkDiscountDialog from "./UnlinkDiscountDialog.vue";

export default {
  components: {
    "unlink-discount-dialog": UnlinkDiscountDialog,
  },
  props: {
    pharmacyId: String,
    appliedDiscounts: Object
  },
  data: () => ({
    isLoading: false,
    isUnlinkDiscountDialogOpen: false,
    discounts: [],
    selectedDiscountId: null,
    headers: [
      { text: 'Erstellt am', value: 'created', sortable: true },
      { text: 'Termindatum', value: 'date', sortable: true },
      { text: 'Zeit', value: 'time', sortable: true },
      { text: 'Gutschrift Wert', value: 'mediation_charge', sortable: true },
      { text: 'Gutschrift Text', value: 'mediation_charge_description', sortable: false },
      { text: 'Status', value: 'wf_state', sortable: true },
    ],
    oneTimeDiscountsAppointments: [],
  }),
  methods: {
    translateWfState(state) {
      const states = {
        initial: 'Entwurf',
        inquired: 'angefragt',
        open: 'in Bearbeitung',
        allocated: 'vergeben',
        apobilanz: 'Apobilanz',
        tempInvoice: 'in vorl. Rechnung',
        invoice: 'in Rechnung',
        paid: 'bezahlt',
        expired: 'abgelaufen',
        deleted: 'gelöscht',
      };
      return states[state] || state; // Fallback: Originalwert, falls nicht übersetzt
    },
    async fetchAllDiscounts() {
      try {
        const response = await axios.get("/api/discounts/");
        this.discounts = response.data
                          .map((discount) => ({ text: discount.discountName, value: discount._id }))
                          .filter((discount)=> !this.appliedDiscounts[discount.value]) || [];
      } catch (error) {
        this.alertError();
      }
    },
    async fetchAllOneTimeDiscounts() {
      this.isLoading = true;
      try {
        //consider paging and sort
        const response = await axios.get(
          `/api/discounts/onetimediscounts/${this.pharmacyId}`,
        );

        this.oneTimeDiscountsAppointments = response.data.appointments;
      } catch (error) {
        this.alertError();
      } finally {
        this.isLoading = false;
      }
    },
    async assignDiscountToPharmacy() {
      try{
        if (this.selectedDiscountId && this.pharmacyId) {
          const response = await axios.get(`/api/discounts/${this.selectedDiscountId}/assign_to/${this.pharmacyId}`);
          if (response.data.status >= 400) throw new Error();
          await this.fetchAllDiscounts();
          this.alertSuccess("Der Rabatt wurde erfolgreich verknüpft");
          window.location.reload();
          return;
        }
        this.alertError("Bitte wähle eine Apotheke aus, um fortzufahren");
      } catch (error) {
        this.alertError();
      }
    },
    translateDiscountType(discountType) {
      if (discountType === "percentage") return "Prozent";
      if (discountType === "number") return "Nummer";
      if (discountType === "fixprice") return "Fixpreis";
      return "";
    },
    discountValueDisplay(discount) {
      if (discount?.discountType !== "fixprice") {
        return discount?.discountValue;
      }
      return this.formatFixPriceValues(discount, "fixPrice");
    },
    flatRateDiscountValueDisplay(discount) {
      if (discount?.flatRateDiscountType !== "fixprice") {
        return discount?.flatRateDiscountValue;
      }
      return this.formatFixPriceValues(discount, "flatRateFixPrice");
    },
    formatFixPriceValues(discount, prefix) {
      const fields = [
        ["Apotheker", `${prefix}SubstitutionBaseRate`, `${prefix}SubstitutionMargin`],
        ["PTA", `${prefix}PtaBaseRate`, `${prefix}PtaMargin`],
        ["PKA", `${prefix}PkaBaseRate`, `${prefix}PkaMargin`],
      ];
      return fields
        .map(([label, baseKey, marginKey]) => `${label}: ${this.formatFixPriceValue(discount?.[baseKey])} / ${this.formatFixPriceValue(discount?.[marginKey])}`)
        .join(", ");
    },
    formatFixPriceValue(value) {
      return value !== null && value !== undefined && value !== "" ? value : "-";
    },
    ...mapMutations({
      alertError: "alert/error",
      alertSuccess: "alert/success",
    }),
  },
  async created() {
    await this.fetchAllDiscounts();
    await this.fetchAllOneTimeDiscounts();
  },
};
</script>

<style scoped>
.container {
  width: 100%;
}
.new-subscription {
  display: grid;
  grid-template-columns: 8fr 4fr;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
}

th {
  vertical-align: middle;
  padding: 0 10px !important;
  border-bottom: none !important;
  font-size: 15px !important;
  color: black !important;
}

.discount-table {
  border: 1px solid #EEE;
}

.no-discounts {
  text-align: center;
  width: 100%;
  padding: 1rem;
  background: #FFFFFF;
  border: 1px solid #F0F0F0;
}
</style>
